import { createRouter as createTrackedRouter } from '@datadog/browser-rum-react/tanstack-router';
import { createRouter, type RouterHistory } from '@tanstack/react-router';

import queryClient from '@@api/queryClient';
import { type GeneeaEntity } from '@@api/services/metadata/schemas';
import Loader from '@@components/Loader';
import { Error404 } from '@@containers/App/Error404';
import { ErrorPage } from '@@containers/App/ErrorPage';
import { type RouterContext } from '@@routes/__root';
import { routeTree } from '@@scripts/routeTree.gen';
import { useSettingsStore } from '@@scripts/store/settings/useSettingsStore';
import { isDataDogEnabled } from '@@utils/dataDog';

import { getLoginRedirect } from './login';

type AppRouterOptions = {
    history?: RouterHistory;
    context?: Partial<RouterContext>;
};

/** The app's router. Tests create their own with a memory history and replace what leaves the app. */
export const createAppRouter = ({ context, ...options }: AppRouterOptions = {}) => {
    // Datadog's wrapper starts a RUM view named after the route on each change of path. It queues its
    // callbacks until RUM starts, which never happens in dev and tests, so only RUM gets it.
    const create = isDataDogEnabled() ? createTrackedRouter : createRouter;
    const router = create({
        routeTree,
        context: { queryClient, getLoginRedirect, ...context },
        // Query owns caching, so the router always calls loaders and lets Query decide whether to fetch
        defaultPreloadStaleTime: 0,
        // and keeps no page after the user leaves it. A page kept would reuse the query options its
        // `context` built on the first visit, with the content language of that time.
        defaultGcTime: 0,
        // Errors replace the page that failed, so the navigation stays usable
        defaultErrorComponent: ErrorPage,
        // For a URL no route matches, and an item a loader didn't find. The URL stays.
        defaultNotFoundComponent: Error404,
        // Shown when a page takes longer than this to load, including the first one
        defaultPendingComponent: Loader,
        defaultPendingMs: 500,
        // and goes as soon as the page is ready. A minimum would hold back a page ready just after it shows.
        defaultPendingMinMs: 0,
        ...options,
    });

    // Mirror the pathname for code outside React, such as the Content-Language header in `api/headers.ts`.
    // `onBeforeLoad` fires before the new page renders, so its first queries already see the new pathname.
    router.subscribe('onBeforeLoad', ({ toLocation }) => {
        useSettingsStore.getState().setPathname(toLocation.pathname);
    });

    return router;
};

declare module '@tanstack/react-router' {
    interface Register {
        router: ReturnType<typeof createAppRouter>;
    }

    interface HistoryState {
        /** The search of the list a detail page was opened from, to return to it */
        lastResourceListSearch?: Record<string, unknown>;
        parentId?: number;
        menuNodeId?: string;
        /** The row a Geneea entity was opened from, as the API has no endpoint for one entity */
        geneeaEntity?: GeneeaEntity;
    }
}
